import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from "react";

import { Row, Col, Image } from "react-bootstrap";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  Autocomplete,
  InputAdornment,
  Skeleton,
  Checkbox
} from "@mui/material";

import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Search } from "@mui/icons-material";

import Select from '../PCSelect';
import useIsMobile from '../../helper/useIsMobile';
import { COLORS } from '../../Pages/admin/shared/tokens';

import NoRecordFound from "../../assets/images/norecordfound.png";

/* ─── brand tokens (sourced from shared/tokens.js) ───────────── */
const RED       = COLORS.primary;
const RED_DEEP  = COLORS.primaryStrong;
const CARD_BG   = COLORS.surface;
const CARD_BDR  = COLORS.border;
const TH_BG     = COLORS.surfaceAlt;
const TR_HOVER  = COLORS.primaryTintSoft;
const TR_BDR    = COLORS.border;
const TEXT_MAIN = COLORS.text;
const TEXT_DIM  = COLORS.textDim;
const INPUT_BG  = COLORS.surface;

const pageSizes = [
  { label: 50,  value: 50  },
  { label: 100, value: 100 },
  { label: 200, value: 200 },
  { label: 500, value: 500 }
];

const defaultPagination = {
  pageSize: 50,
  recordOffset: 1,
  totalPages: 1,
  totalItems: 0
};

const defaultTableSkeleton = [...Array(5)].map((_, i) => (
  <TableCell key={i}>
    <Skeleton height={28} sx={{ bgcolor: 'rgba(0,0,0,0.06)', borderRadius: '6px' }} />
  </TableCell>
));

/* ─── react-select dark theme styles ───────────────────────── */
const rsStyles = {
  container: base => ({ ...base, width: 130 }),
  control: base => ({
    ...base,
    background: INPUT_BG,
    borderColor: 'rgba(0,0,0,0.15)',
    boxShadow: 'none',
    minHeight: '34px',
    '&:hover': { borderColor: RED },
  }),
  menu: base => ({ ...base, background: '#fff', border: `1px solid ${CARD_BDR}`, zIndex: 100000 }),
  menuPortal: base => ({ ...base, zIndex: 100000 }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? COLORS.primaryTintSoft : 'transparent',
    color: state.isFocused ? RED : TEXT_MAIN,
    fontSize: '13px',
  }),
  singleValue: base => ({ ...base, color: TEXT_MAIN, fontSize: '13px' }),
  dropdownIndicator: base => ({ ...base, color: TEXT_DIM }),
  indicatorSeparator: base => ({ ...base, background: 'rgba(0,0,0,0.12)' }),
};

const PhoneClinicGrid = forwardRef((props, ref) => {

  const [tags, setTags]                   = useState([]);
  const [searchData, setSearchData]       = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState([]);
  const [pagination, setPagination]       = useState(defaultPagination);
  const isFirstRender                     = useRef(true);
  const [loading, setLoading]             = useState(true);

  const tableHeadData = props.tableHeadData?.length > 0 ? props.tableHeadData : [];

  /* ── PHONE CARDS — added 2026-10-26 ────────────────────────────────────────
     Below 768px each row renders as a card: one "LABEL  value" line per cell, the
     action icons along the bottom (CSS in assets/css/mobile.css). The labels come
     from tableHeadData, added to each cell as data-label. At 768px and wider none
     of this runs: the grid takes exactly the path it took before. (There, a list too
     wide for its space is turned into cards by helper/fitCards.js, labelled from the
     header row by helper/cardTableLabels.js — "pc-grid-table" marks it for that.) */
  const isMobile = useIsMobile();
  const cardLabels = isMobile ? [
    ...(props.selection && props.selection.onChange ? ['Select'] : []),
    ...tableHeadData.filter(col => !col.isHidden).map(col => (typeof col.label === 'string' ? col.label : '')),
    ...(props.isEditable ? ['Action'] : []),
  ] : null;
  /* Labels go on only when the row has exactly one cell per column; a row built some
     other way still shows as a card, just without labels, rather than mislabelled. */
  const withCardLabels = (cells) => {
    const list = React.Children.toArray(cells).filter(React.isValidElement);
    if (!cardLabels || list.length !== cardLabels.length) return list;
    return list.map((cell, i) => React.cloneElement(cell, { 'data-label': cardLabels[i] }));
  };

  /* ── ROW SELECTION — opt-in, added 2026-10-13 ──────────────────────────────
     37 screens render this grid. Pass no `selection` prop and every one of them
     takes exactly the path it took before: no extra column, no extra header
     cell, no behaviour change. Nothing below runs unless a screen opts in.

     The parent still owns the rows (they arrive as `children`), so it has to
     tell us which id each row carries — it does that with a data-pcid
     attribute on its <TableRow>. We read that rather than guessing from the
     row's contents.

       selection = {
         rowIds:        ids on the current page, in render order
         selectableIds: subset that may be ticked (default: all of rowIds)
         selected:      ids currently ticked
         onChange:      (nextSelectedIds) => void
       }
  */
  const sel = props.selection && props.selection.onChange ? props.selection : null;
  const selRowIds = sel ? (sel.rowIds || []).map(String) : [];
  const selSelectable = sel
    ? (sel.selectableIds ? sel.selectableIds.map(String) : selRowIds)
    : [];
  const selSelected = sel ? (sel.selected || []).map(String) : [];
  // Select-all applies to the selectable rows on THIS page only — never to rows
  // the user cannot see, which is the trap with a server-paginated grid.
  const selAllOn = selSelectable.length > 0
    && selSelectable.every(id => selSelected.includes(id));
  const selSomeOn = selSelected.length > 0 && !selAllOn;

  const toggleAll = () => {
    if (!sel) return;
    // Keep any selected ids from other pages intact.
    const offPage = selSelected.filter(id => !selRowIds.includes(id));
    sel.onChange(selAllOn ? offPage : [...offPage, ...selSelectable]);
  };
  const toggleOne = (id) => {
    if (!sel) return;
    const key = String(id);
    sel.onChange(selSelected.includes(key)
      ? selSelected.filter(x => x !== key)
      : [...selSelected, key]);
  };

  /* The ONE header-cell style. It used to be written out three times (here, and inline on the
     column cells and on the ACTION cell). `whiteSpace: normal` because the grid no longer scrolls
     sideways — a long label wraps instead of widening the table. */
  const headCellSx = {
    background: TH_BG,
    color: TEXT_DIM,
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    fontFamily: "'Inter', sans-serif",
    borderBottom: `1px solid ${TR_BDR}`,
    whiteSpace: 'normal',
  };

  /* ── tags → searchData ── */
  useEffect(() => {
    setSearchData(tags.map((tag, i) => ({ key: `search_${i}`, value: tag })));
    setPagination(prev => ({ ...prev, recordOffset: 1 }));
  }, [tags]);

  /* ── debounce ── */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchData), 500);
    return () => clearTimeout(timer);
  }, [searchData]);

  /* ── main API trigger ── */
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (!props.getData) return;
    const fetchData = async () => {
      await props.getData(debouncedSearch, pagination);
      setLoading(false);
    };
    fetchData();
  }, [debouncedSearch, pagination.pageSize, pagination.recordOffset]);

  /* ── pagination ── */
  const changePage     = page   => setPagination(prev => ({ ...prev, recordOffset: page }));
  const changePageSize = option => setPagination(prev => ({ ...prev, pageSize: option.value, recordOffset: 1 }));

  /* ── expose ref ── */
  useImperativeHandle(ref, () => ({
    refresh() { if (props.getData) props.getData(searchData, pagination); },
    setPaginationData(data) { setPagination(data); }
  }));

  return (
    <div className={isMobile ? 'pc-grid-card--mobile' : undefined} style={isMobile ? {
      margin: '12px',
      background: CARD_BG,
      border: `1px solid ${CARD_BDR}`,
      borderRadius: '8px',
      overflow: 'visible',
      boxSizing: 'border-box',
      boxShadow: 'none',
    } : {
      margin: '16px',
      background: CARD_BG,
      border: `1px solid ${CARD_BDR}`,
      borderRadius: '8px',
      /* NOT 'hidden'. position: sticky pins to the nearest box that clips or scrolls, so a clipping
         card would hold the header inside itself and it would never stick to the page. */
      overflow: 'visible',
      /* Normally full width. A table that cannot fit on one line per cell turns into cards
         (helper/fitCards.js, 2026-10-27), so nothing scrolls sideways; while it measures the table
         this lets the card grow to the table's full width (the measurement is never painted). */
      width: 'fit-content',
      minWidth: 'calc(100% - 32px)',
      boxSizing: 'border-box',
      boxShadow: 'none',
    }}>

      {/* ── card header ── */}
      <div style={isMobile ? {
        padding: '12px 14px',
        borderBottom: `1px solid ${CARD_BDR}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '10px',
        background: '#ffffff',
      } : {
        padding: '14px 20px',
        borderBottom: `1px solid ${CARD_BDR}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#ffffff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {/* red accent bar */}
          <div style={{
            width: '4px', height: '20px', borderRadius: '4px',
            background: RED,
            flexShrink: 0,
          }} />
          <span style={{
            fontSize: '13px', fontWeight: '700', color: '#111',
            letterSpacing: '1.5px', textTransform: 'uppercase',
            fontFamily: "'Inter', sans-serif",
          }}>
            {props.title}
          </span>
          <span style={{ fontSize: '11px', color: TEXT_DIM, fontFamily: "'Inter', sans-serif" }}>
            SEARCH
          </span>
        </div>

        {props.buttonLabel && (
          <button
            onClick={props.onClickButton}
            style={{
              background: RED,
              color: 'white', border: 'none',
              padding: '7px 18px', borderRadius: '8px',
              ...(isMobile ? { minHeight: '44px' } : {}),
              fontSize: '11px', letterSpacing: '1px', fontWeight: '600',
              cursor: 'pointer', textTransform: 'uppercase',
              fontFamily: "'Inter', sans-serif",
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = RED_DEEP}
            onMouseLeave={e => e.currentTarget.style.background = RED}
          >
            {props.buttonLabel}
          </button>
        )}
      </div>

      {/* ── body ── */}
      <div style={{ padding: isMobile ? '12px 12px 0' : '14px 16px 0' }}>

        {/* SEARCH */}
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={tags}
          onChange={(e, newValue) => setTags(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                size="small"
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                key={index}
                sx={{
                  color: RED,
                  borderColor: `${RED}66`,
                  fontSize: '11px',
                  '& .MuiChip-deleteIcon': { color: `${RED}88` },
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Search…"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <Search sx={{ color: TEXT_DIM, fontSize: '18px' }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={{
                marginBottom: '12px',
                '& .MuiOutlinedInput-root': {
                  background: INPUT_BG,
                  borderRadius: '10px',
                  color: TEXT_MAIN,
                  fontSize: '13px',
                  fontFamily: "'Inter', sans-serif",
                  '& fieldset': { borderColor: CARD_BDR },
                  '&:hover fieldset': { borderColor: RED_DEEP },
                  '&.Mui-focused fieldset': { borderColor: RED, borderWidth: '1px' },
                },
                '& input::placeholder': { color: TEXT_DIM, opacity: 1 },
              }}
            />
          )}
        />

        {/* TABLE */}
        {/* ONE SCROLL (2026-10-22). This container used to cap itself at 60vh and scroll inside the
            page, which already scrolls (div.content in AdminLayout) — two scrollbars. Now it never
            scrolls in either direction: rows grow the page, and cells wrap rather than overflow
            sideways. stickyHeader then pins the column header to the PAGE, just under the top bar.
            overflow must stay 'visible' for that — any clip/scroll here would capture the sticky. */}
        {/* 2026-10-28: no frame or rounded corners here — every admin table now draws a full grid
            (admin-theme.css, user's choice), so the table's own outer line is the frame; a border
            here as well doubled it. */}
        <TableContainer sx={{
          background: 'transparent',
          overflow: 'visible',
        }}>
          <Table stickyHeader size="small" className={isMobile ? 'pc-grid-table pc-grid-cards' : 'pc-grid-table'} sx={{
            width: '100%',
          }}>

            <TableHead>
              <TableRow>
                {sel && (
                  <TableCell padding="checkbox" sx={headCellSx}>
                    <Checkbox
                      size="small"
                      checked={selAllOn}
                      indeterminate={selSomeOn}
                      disabled={selSelectable.length === 0}
                      onChange={toggleAll}
                      inputProps={{ 'aria-label': 'Select all rows on this page' }}
                      sx={{ color: TEXT_DIM, '&.Mui-checked': { color: RED }, '&.MuiCheckbox-indeterminate': { color: RED } }}
                    />
                  </TableCell>
                )}
                {props.tableHeadData?.map((col, i) =>
                  !col.isHidden && (
                    <TableCell key={i} align={col.align || "left"} sx={headCellSx}>
                      {col.label}
                    </TableCell>
                  )
                )}
                {props.isEditable && (
                  <TableCell align="center" sx={headCellSx}>
                    ACTION
                  </TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                [...Array(20)].map((_, i) => (
                  <TableRow key={i} sx={{ background: 'transparent' }}>
                    {sel && (
                      <TableCell padding="checkbox" sx={{ borderBottom: `1px solid ${TR_BDR}` }} />
                    )}
                    {tableHeadData.length === 0
                      ? defaultTableSkeleton
                      : props.tableHeadData?.map((col, index) =>
                          !col.isHidden && (
                            <TableCell key={index} sx={{ borderBottom: `1px solid ${TR_BDR}` }}>
                              <Skeleton height={24} sx={{ bgcolor: 'rgba(0,0,0,0.05)', borderRadius: '5px' }} />
                            </TableCell>
                          )
                        )
                    }
                    {props.isEditable && (
                      <TableCell sx={{ borderBottom: `1px solid ${TR_BDR}` }}>
                        <Skeleton height={24} sx={{ bgcolor: 'rgba(0,0,0,0.05)', borderRadius: '5px' }} />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                /* inject sx into each child TableRow via React.Children.map */
                React.Children.map(props.children, (child) => {
                  if (!child) return child;

                  /* With selection on, prepend a checkbox cell to the row the parent
                     rendered. The id comes from the row's data-pcid attribute; a row
                     without one gets a blank cell, so the columns still line up and a
                     screen that forgets the attribute degrades visibly rather than
                     silently selecting the wrong record. */
                  const rowId = sel ? child.props['data-pcid'] : undefined;

                  /* `children` is added to the clone ONLY when selection is on. With it off the
                     config is exactly what it was before this feature, so the other 32 screens
                     keep their original children by reference and cannot be affected. */
                  const extra = sel ? {
                    children: (isMobile ? withCardLabels : (cells => cells))([
                      <TableCell padding="checkbox" key="__pcsel">
                        {rowId !== undefined && rowId !== null && selSelectable.includes(String(rowId)) ? (
                          <Checkbox
                            size="small"
                            checked={selSelected.includes(String(rowId))}
                            onChange={() => toggleOne(rowId)}
                            inputProps={{ 'aria-label': `Select row ${rowId}` }}
                            sx={{ color: TEXT_DIM, '&.Mui-checked': { color: RED } }}
                          />
                        ) : null}
                      </TableCell>,
                      ...React.Children.toArray(child.props.children),
                    ]),
                  } : isMobile ? { children: withCardLabels(child.props.children) } : {};

                  return React.cloneElement(child, {
                    ...extra,
                    sx: {
                      ...(child.props.sx || {}),
                      '&:hover': { background: TR_HOVER },
                      '& td, & th': {
                        borderBottom: `1px solid ${TR_BDR}`,
                        color: TEXT_MAIN,
                        fontSize: '13px',
                        fontFamily: "'Inter', sans-serif",
                        /* Wrap at spaces rather than widen the table. Most screens put
                           style={{ whiteSpace: 'nowrap' }} on their <TableRow>; cells only INHERIT
                           that, so this explicit value wins without editing those screens.
                           Words are never split: 'break-word', not 'anywhere'. 'anywhere' let the
                           browser shrink every column to one character, so crowded screens read
                           "Ph|on|e" — the user chose a sideways page scroll over that (2026-10-22). */
                        /* 2026-10-27: overridden — the user wants NO wrapping at all; index.css forces
                           every admin table cell onto one line (nowrap !important) and the page
                           scrolls sideways when a table is too wide. On a phone, mobile.css cards. */
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                      },
                      '&:last-child td, &:last-child th': { borderBottom: 0 },
                    },
                  });
                })
              )}
            </TableBody>

          </Table>

          {!loading && React.Children.count(props.children) === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
              <Image src={NoRecordFound} alt="No record found" style={{ opacity: 0.35, filter: 'grayscale(1)', maxWidth: '120px' }} />
              <div style={{ color: TEXT_DIM, fontSize: '13px', marginTop: '12px', fontFamily: "'Inter', sans-serif", letterSpacing: '1px' }}>
                NO RECORDS FOUND
              </div>
            </div>
          )}
        </TableContainer>

        {/* PAGINATION */}
        <div style={isMobile ? {
          display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '10px',
          alignItems: 'center', padding: '12px 0',
        } : {
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', padding: '12px 4px',
        }}>
          <Select
            value={{ label: pagination.pageSize, value: pagination.pageSize }}
            options={pageSizes}
            onChange={changePageSize}
            styles={rsStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            menuPlacement="auto"
          />

          <Pagination
            count={pagination.totalPages}
            page={pagination.recordOffset}
            onChange={(e, page) => changePage(page)}
            {...(isMobile ? { siblingCount: 0 } : {})}
            renderItem={(item) => (
              <PaginationItem
                components={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                {...item}
              />
            )}
            sx={{
              '& .MuiPaginationItem-root': {
                color: TEXT_DIM,
                fontSize: '12px',
                fontFamily: "'Inter', sans-serif",
                border: `1px solid ${CARD_BDR}`,
                borderRadius: '4px',
                '&:hover': { background: TR_HOVER, color: RED, borderColor: RED },
                '&.Mui-selected': {
                  background: RED,
                  color: 'white',
                  border: 'none',
                },
              },
            }}
          />
        </div>

      </div>
    </div>
  );
});

export default PhoneClinicGrid;
